import { Command, Option } from "clipanion";
import { M8tCommand } from "../../lib/m8t-command.js";
import { LocalCliError } from "../../lib/errors.js";
import { colors } from "../../lib/output.js";
import { readBootstrapState } from "../../lib/bootstrap-state.js";
import { readStatusBlob } from "../../lib/bootstrap-status.js";
import { reapInstaller, sweepOrphanOwnerAssignments } from "../../lib/bootstrap-reap.js";
import { getAciState } from "../../lib/bootstrap-aci-state.js";
import { getAzAccount } from "../../lib/auth.js";

export class BootstrapReapCommand extends M8tCommand {
  static paths = [["bootstrap", "reap"]];
  static usage = Command.Usage({
    description: "Tear down the installer scaffolding (ACI → MI → its role assignments) after a successful install.",
    details: "Runs locally on the 'done' signal (the installer can't delete its own identity). The platform RG and the gateway's own assignments persist. A failed install is left intact for diagnosis unless --force.",
    examples: [["Reap after done", "$0 bootstrap reap"]],
  });

  force = Option.Boolean("--force", false);
  sweepOrphans = Option.Boolean("--sweep-orphans", false);
  yes = Option.Boolean("--yes", false);

  async executeCommand(): Promise<number> {
    if (this.sweepOrphans === true) {
      const { subscriptionId: sub } = await getAzAccount();
      if (!sub) {
        throw new LocalCliError({ code: "BOOTSTRAP_SWEEP_NO_SUB", message: "No active subscription.", hint: "Run 'az account set --subscription <id>'." });
      }
      const found = await sweepOrphanOwnerAssignments({ subscriptionId: sub, execute: this.yes === true });
      if (found.length === 0) {
        this.context.stdout.write(`${colors.success("✓")} No orphaned installer Owner@sub assignments found.\n`);
        return 0;
      }
      for (const f of found) {
        this.context.stdout.write(`  ${f.id}${f.createdOn ? colors.dim(` (created ${f.createdOn})`) : ""}${f.description ? colors.dim(" [tagged]") : ""}\n`);
      }
      if (this.yes === true) {
        this.context.stdout.write(`${colors.success("✓")} Removed ${String(found.length)} orphaned Owner@sub assignment(s).\n`);
      } else {
        this.context.stdout.write(`\nFound ${String(found.length)} orphaned Owner@sub assignment(s) (dry-run). ${colors.hint("Re-run with --sweep-orphans --yes to delete them.")}\n`);
      }
      return 0;
    }

    const state = await readBootstrapState();
    if (!state) {
      // A launch that failed BEFORE writing state (e.g. an inaccessible installer
      // image right after the MI + Owner@sub grant) leaves no local record but may
      // have orphaned that privileged grant. Without --force we can't know there's
      // anything to clean, so --force self-heals by sweeping the tagged orphans.
      if (this.force === true) {
        const { subscriptionId: sub } = await getAzAccount();
        if (!sub) {
          throw new LocalCliError({ code: "BOOTSTRAP_SWEEP_NO_SUB", message: "No active subscription.", hint: "Run 'az account set --subscription <id>'." });
        }
        const found = await sweepOrphanOwnerAssignments({ subscriptionId: sub, execute: true });
        this.context.stdout.write(
          found.length === 0
            ? `${colors.success("✓")} No bootstrap state and no orphaned installer Owner@sub assignments. Nothing to reap.\n`
            : `${colors.success("✓")} No bootstrap state found; swept ${String(found.length)} orphaned installer Owner@sub assignment(s).\n`,
        );
        return 0;
      }
      throw new LocalCliError({
        code: "BOOTSTRAP_NO_STATE",
        message: "No bootstrap state to reap.",
        hint: "Nothing to do. If a launch failed early it may have orphaned an installer Owner@sub grant — run 'm8t bootstrap reap --force' (or 'm8t bootstrap reap --sweep-orphans --yes') to clean it.",
      });
    }
    // With --force we tear down regardless of install status AND without requiring a
    // readable status blob — an early-failed install may never have created the status
    // storage account, and reading it would throw before we could reap. Only gate on
    // status when NOT forcing.
    if (this.force !== true) {
      const doc = await readStatusBlob({ saName: state.statusSaName, resourceGroup: state.resourceGroup, subscriptionId: state.subscriptionId });
      if (doc.status !== "done") {
        if (doc.status === "failed") {
          throw new LocalCliError({
            code: "BOOTSTRAP_REAP_FAILED_INSTALL",
            message: `The install failed at '${doc.error?.phase ?? doc.phase}'. Leaving the installer up for diagnosis.`,
            hint: `Inspect: az container logs -n ${state.aciName} -g ${state.resourceGroup}. Reap anyway with --force.`,
          });
        }
        const aci = await getAciState({ aciName: state.aciName, resourceGroup: state.resourceGroup, subscriptionId: state.subscriptionId }).catch(() => null);
        const deadHint = aci?.terminated
          ? `The installer container has terminated (exit ${String(aci.exitCode ?? "?")}) without reaching done — reap with --force.`
          : `Wait for 'm8t bootstrap status --watch' to reach done, or reap with --force if the install is stuck.`;
        throw new LocalCliError({
          code: "BOOTSTRAP_REAP_NOT_DONE",
          message: `The install is not done yet (status: ${doc.status}, phase: ${doc.phase}).`,
          hint: deadHint,
        });
      }
    }
    await reapInstaller({
      subscriptionId: state.subscriptionId, resourceGroup: state.resourceGroup,
      aciName: state.aciName, miName: state.miName, roleAssignmentIds: state.roleAssignmentIds,
      principalId: state.miPrincipalId,
    });
    this.context.stdout.write(`${colors.success("✓")} reaped the installer (ACI + identity + its role assignments). The platform stays.\n`);
    this.context.stdout.write(`  ${colors.hint("next:")} m8t bootstrap finish\n`);
    return 0;
  }
}
